import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';

interface MarkdownContentProps {
  children: string;
  className?: string;
  componentOverrides?: Partial<Components>;
}

const defaultComponents: Partial<Components> = {
  p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside pl-6 mb-3 space-y-1.5 [&>li]:pl-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside pl-6 mb-3 space-y-1.5 [&>li]:pl-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-semibold mt-4 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h3>,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  img: ({ src, alt }) => (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block my-2">
      <img src={src} alt={alt || 'Screenshot'} className="max-w-full max-h-96 rounded-md border object-contain" loading="lazy" />
    </a>
  ),
  code: ({ className, children, ...props }) => {
    const isInline = !className;
    return isInline ? (
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm" {...props}>
        {children}
      </code>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="mb-2 overflow-x-auto rounded-md bg-muted p-3 text-sm">{children}</pre>,
};

export function MarkdownContent({ children, className, componentOverrides }: MarkdownContentProps) {
  const components = componentOverrides ? { ...defaultComponents, ...componentOverrides } : defaultComponents;
  return (
    <div className={className}>
      <ReactMarkdown components={components}>{children}</ReactMarkdown>
    </div>
  );
}
